import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import { Box, CardMedia } from '@mui/material'
import React, { useRef, useState } from 'react'
import { logEvent } from '../../../../analytics/analytics'
import { DEFAULT_CARD_IMAGE } from '../../../../helpers/static-constants'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import { selectRestaurantDetail } from '../../../../store/restaurantSlice'
import {
  RestaurantImagesMainContainer,
  RestaurantImagesMainImgCard,
  RestaurantImagesMainImgWrapper,
  RestaurantImagesOtherImgsCard,
  RestaurantImagesOtherImgsInnerWrapper,
  RestaurantImagesOtherImgsWrapper,
  RestaurantImagesSkeleton,
  RestaurantImagesSmallScreenImgsCard,
  RestaurantImagesSmallScreenImgsWrapper,
} from '../styled/RestaurantImages.styled'

const RestaurantImages: React.FC = () => {
  const restaurant = useAppSelector(selectRestaurantDetail)?.restaurantDTO
  const { isMediumScreen, theme } = useAppUtilities()
  const [loaded, setLoaded] = useState(new Array(5).fill(false))

  // Handle both imageUrl (new) and imageurl (legacy) for backward compatibility
  const getRestaurantImageUrl = (rest: typeof restaurant): string => {
    if (rest?.imageUrl && rest.imageUrl.length !== 0) {
      return rest.imageUrl
    }
    // Fallback to lowercase imageurl in case API still returns it
    const legacyImageUrl = (rest as any)?.imageurl
    if (legacyImageUrl && legacyImageUrl.length !== 0) {
      return legacyImageUrl
    }
    return DEFAULT_CARD_IMAGE
  }

  const imageUrl = getRestaurantImageUrl(restaurant)

  const isLogoFallback =
    !imageUrl || imageUrl === DEFAULT_CARD_IMAGE || imageUrl === ''
  const hasRealOtherImages = (restaurant?.restaurantImages ?? []).some(
    (img) => img.url && img.url !== DEFAULT_CARD_IMAGE && img.url !== '',
  )
  const hasRealImages = !isLogoFallback || hasRealOtherImages

  const baseImages = [{
    src: imageUrl,
    alt: 'resactly-restaurant-image',
  }];

  const otherImages = restaurant?.restaurantImages?.map(image => ({
    src: image.url ?? imageUrl,
    alt: `resactly-restaurant-image-${image.order}`,
  })) ?? [];

  const extraImages = Array.from({ length: Math.max(0, 4 - otherImages.length) }, (_, i) => ({
    src: imageUrl,
    alt: `resactly-restaurant-image-placeholder-${i + 1}`,
  }));

  const images = baseImages.concat(otherImages, extraImages);

  // gallery_viewed: a tap anywhere in the photo gallery is a real "looking closer" interaction.
  // Fired at most once per mount so a browsing session counts as one gallery view, not one per photo.
  const galleryViewedRef = useRef(false)
  const handleGalleryInteract = (): void => {
    if (galleryViewedRef.current) return
    galleryViewedRef.current = true
    logEvent('gallery_viewed', {
      restaurant_id: restaurant?.restaurantId ?? '',
      image_count: images.length,
      screen_name: 'restaurant_detail',
    })
  }

  const handleImageLoad = (index: number): void => {
    setLoaded((prevLoaded) => {
      const newLoaded = [...prevLoaded]
      newLoaded[index] = true
      return newLoaded
    })
  }

  if (!hasRealImages) {
    return (
      <Box
        sx={{
          width: '100%',
          height: { xs: '200px', md: '300px' },
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.default,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <StorefrontOutlinedIcon
          sx={{ fontSize: 56, color: theme.palette.text.disabled }}
        />
      </Box>
    )
  }

  return (
    <>
      {!isMediumScreen ? (
        <RestaurantImagesMainContainer onClick={handleGalleryInteract}>
          <RestaurantImagesMainImgWrapper>
            <RestaurantImagesMainImgCard>
              {!loaded[0] && <RestaurantImagesSkeleton />}
              <CardMedia
                sx={{
                  height: '100%',
                  width: '100%',
                  display: loaded[0] ? 'block' : 'none',
                }}
                component="img"
                image={images[0].src}
                alt={images[0].alt}
                onLoad={() => handleImageLoad(0)}
              />
            </RestaurantImagesMainImgCard>
          </RestaurantImagesMainImgWrapper>
          <RestaurantImagesOtherImgsWrapper>
            {images.slice(1).map((image, index) => (
              <RestaurantImagesOtherImgsInnerWrapper key={index}>
                <RestaurantImagesOtherImgsCard>
                  {!loaded[index + 1] && <RestaurantImagesSkeleton />}
                  <CardMedia
                    sx={{
                      height: '100%',
                      width: '100%',
                      display: loaded[index + 1] ? 'block' : 'none',
                    }}
                    component="img"
                    image={image.src}
                    alt={image.alt}
                    onLoad={() => handleImageLoad(index + 1)}
                  />
                </RestaurantImagesOtherImgsCard>
              </RestaurantImagesOtherImgsInnerWrapper>
            ))}
          </RestaurantImagesOtherImgsWrapper>
        </RestaurantImagesMainContainer>
      ) : (
        <RestaurantImagesSmallScreenImgsWrapper onClick={handleGalleryInteract}>
          {images.map((image, index) => (
            <RestaurantImagesSmallScreenImgsCard key={index}>
              {!loaded[index] && <RestaurantImagesSkeleton />}
              <CardMedia
                sx={{
                  height: '100%',
                  width: '100%',
                  display: loaded[index] ? 'block' : 'none',
                }}
                component="img"
                image={image.src}
                alt={image.alt}
                onLoad={() => handleImageLoad(index)}
              />
            </RestaurantImagesSmallScreenImgsCard>
          ))}
        </RestaurantImagesSmallScreenImgsWrapper>
      )}
    </>
  )
}

export default RestaurantImages
